import React, { useEffect, useState } from "react";
import { plyxApi as api } from "@/lib/plyxApi";
import { Sparkles, Trophy } from "lucide-react";

/**
 * LiveHourBadge — surfaces the weekly Plyx Live Hour event.
 *
 * Polls `/api/plyx/live-hour` every 60s. While the window is ACTIVE, shows
 * a vibrant glowing banner with a live countdown to the end of the hour.
 * While INACTIVE, shows a compact "next event" pill so creators know when
 * the 2× multiplier kicks in.
 *
 * Also fetches the most recent winner and shows the poster thumbnail when
 * available — gives the discovery page a sense of momentum.
 */
const fmtCountdown = (secs) => {
  if (secs <= 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const fmtDayHour = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
  } catch {
    return iso;
  }
};

const LiveHourBadge = () => {
  const [state, setState] = useState(null);
  const [winner, setWinner] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/plyx/live-hour").then((r) => mounted && setState(r.data)).catch(() => {});
      api.get("/plyx/live-hour/winners?limit=1")
        .then((r) => mounted && setWinner(r.data?.winners?.[0] || null))
        .catch(() => {});
    };
    load();
    const poll = setInterval(load, 60_000);
    const cd = setInterval(() => mounted && setTick((t) => t + 1), 1000);
    return () => { mounted = false; clearInterval(poll); clearInterval(cd); };
  }, []);

  if (!state) return null;

  // Locally tick down the countdowns between polls.
  const liveSecs = Math.max(0, (state.seconds_remaining || 0) - tick);
  const soonSecs = Math.max(0, (state.seconds_until_start || 0) - tick);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const posterUrl = winner?.poster_url ? `${apiBase}${winner.poster_url}` : null;

  if (state.active) {
    return (
      <div
        data-testid="plyx-live-hour-active-banner"
        className="relative overflow-hidden rounded-2xl border border-[#FFCC2E]/60 bg-gradient-to-r from-[#3a1d52] via-[#5e2a8a] to-[#bc4b1a] p-5 mb-6 shadow-[0_18px_42px_-18px_rgba(255,204,46,0.55)]"
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none [background:radial-gradient(circle_at_20%_30%,rgba(255,204,46,0.6),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(255,61,127,0.5),transparent_50%)]" />
        <div className="relative flex items-center gap-4">
          <div className="shrink-0 grid place-items-center w-12 h-12 rounded-full bg-[#FFCC2E] text-black animate-pulse">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest bg-black/40 text-[#FFCC2E]">LIVE NOW</span>
              <h3 className="text-lg sm:text-xl font-black text-white">Plyx Live Hour · {state.multiplier}× tips</h3>
            </div>
            <p className="text-sm text-white/80 mt-1">
              Tip any creator now — every coin counts double on the Live Hour leaderboard. Winner is crowned at top of the hour.
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Ends in</div>
            <div data-testid="plyx-live-hour-countdown" className="text-2xl font-black text-[#FFCC2E] tabular-nums">{fmtCountdown(liveSecs)}</div>
          </div>
        </div>
      </div>
    );
  }

  // Inactive — show a compact "next event" pill + last winner thumbnail.
  return (
    <div
      data-testid="plyx-live-hour-upcoming"
      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 mb-6"
    >
      <div className="shrink-0 grid place-items-center w-9 h-9 rounded-full bg-[#FFCC2E]/15 text-[#FFCC2E]">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white/90">
          <span className="font-bold">Live Hour</span>
          <span className="text-white/60"> · next window </span>
          <span className="font-bold text-[#FFCC2E]">{fmtDayHour(state.starts_at)}</span>
          <span className="text-white/60"> · </span>
          <span className="font-bold">{state.multiplier}× tips</span>
        </div>
      </div>
      {winner && winner.creator && (
        <a
          href={winner.story_url ? `${apiBase}${winner.story_url}` : (posterUrl || "#")}
          target="_blank"
          rel="noreferrer"
          download={winner.story_url ? `plyx-live-hour-${winner.creator.handle}.png` : undefined}
          data-testid="plyx-live-hour-last-winner"
          className="shrink-0 inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
          title={winner.story_url ? "Download story poster (1080×1920) — share to IG" : "View winner poster"}
        >
          <Trophy className="w-3.5 h-3.5 text-[#FFCC2E]" />
          <span className="text-xs text-white/80">
            Last winner: <span className="font-bold text-white">@{winner.creator.handle}</span>
            <span className="text-white/50"> · {winner.coins.toLocaleString()} coins</span>
          </span>
        </a>
      )}
    </div>
  );
};

export default LiveHourBadge;
